import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import axios from 'axios'
import {
  ArrowLeft,
  Check,
  Copy,
  Gift,
  Loader2,
  Sparkles,
  Ticket,
  Users,
  Wallet,
} from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '../components/ui/button'
import BrandLogo from '../components/BrandLogo'
import DashboardButton from '../components/DashboardButton'
import LanguageToggle from '../components/LanguageToggle'
import ThemeMenu from '../components/ThemeMenu'
import ProfileButton from '../components/ProfileButton'
import AppFooter from '../components/AppFooter'
import { useLanguage } from '../contexts/LanguageContext'
import { currencySymbol, formatMoney, getApiUrl } from '../lib/utils'

const API = getApiUrl()

/**
 * The user's own money, as opposed to the per-event wallet.
 *
 * Two things feed it: closing an event moves what it raised here, and referral
 * bonuses land here as they are earned. Balances are kept and shown per
 * currency and never added together — we don't do FX, and a "total" across
 * GEL/USD/EUR would be a number that means nothing.
 */

type Balance = { currency: string; amount: number }
type Entry = {
  entry_id: string
  currency: string
  amount: number
  kind: string
  balance_after: number
  description: string
  event_id: string | null
  created_at: string | null
}
type AccountData = {
  balances: Balance[]
  currencies: string[]
  recent: Entry[]
  entry_count: number
}
type LedgerPage = { total: number; entries: Entry[] }
type ReferralData = {
  code: string
  link: string
  rate: number
  commission_rate: number
  signups: number
  earned: Balance[]
  payouts: number
  referred_by_code: boolean
}

const PAGE_SIZE = 25

const Account = () => {
  const { t, language } = useLanguage()
  const tr = (en: string, ka: string) => (language === 'ka' ? ka : en)

  const [account, setAccount] = useState<AccountData | null>(null)
  const [referral, setReferral] = useState<ReferralData | null>(null)
  const [entries, setEntries] = useState<Entry[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState(false)
  const [copied, setCopied] = useState<'code' | 'link' | null>(null)

  const loadEntries = useCallback(async (offset: number): Promise<LedgerPage> => {
    const { data } = await axios.get(
      `${API}/account/transactions?limit=${PAGE_SIZE}&offset=${offset}`,
      { withCredentials: true },
    )
    return data
  }, [])

  useEffect(() => {
    let active = true
    const load = async () => {
      setLoading(true)
      setError(false)
      try {
        const [accountRes, referralRes, ledger] = await Promise.all([
          axios.get(`${API}/account`, { withCredentials: true }),
          axios.get(`${API}/referral`, { withCredentials: true }),
          loadEntries(0),
        ])
        if (!active) return
        setAccount(accountRes.data)
        setReferral(referralRes.data)
        setEntries(ledger.entries)
        setTotal(ledger.total)
      } catch {
        if (active) setError(true)
      } finally {
        if (active) setLoading(false)
      }
    }
    load()
    return () => {
      active = false
    }
  }, [loadEntries])

  const loadMore = async () => {
    setLoadingMore(true)
    try {
      const ledger = await loadEntries(entries.length)
      setEntries((prev) => [...prev, ...ledger.entries])
      setTotal(ledger.total)
    } catch {
      toast.error(tr('Could not load more', 'დამატებით ჩატვირთვა ვერ მოხერხდა'))
    } finally {
      setLoadingMore(false)
    }
  }

  const copy = (text: string, which: 'code' | 'link') => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopied(which)
        toast.success(which === 'code' ? t.referralCodeCopied : t.referralLinkCopied)
        setTimeout(() => setCopied(null), 1800)
      })
      .catch(() => toast.error(t.errorOccurred))
  }

  const dateFmt = useMemo(
    () =>
      new Intl.DateTimeFormat(language === 'ka' ? 'ka-GE' : 'en-US', {
        dateStyle: 'medium',
        timeStyle: 'short',
      }),
    [language],
  )

  const kindLabel = (kind: string) =>
    ({
      event_closure: t.entryEventClosure,
      referral: t.entryReferral,
      payout: t.entryPayout,
      adjustment: t.entryAdjustment,
    })[kind] || kind

  const kindIcon = (kind: string) =>
    kind === 'referral' ? (
      <Ticket className="h-4 w-4" />
    ) : kind === 'event_closure' ? (
      <Gift className="h-4 w-4" />
    ) : (
      <Wallet className="h-4 w-4" />
    )

  const shell = (children: React.ReactNode) => (
    <div className="min-h-screen bg-celebrate flex flex-col 2xl:[zoom:1.25]">
      <header className="bg-white/80 dark:bg-[#1e1438]/80 backdrop-blur-xl border-b border-[#1b1033]/10 dark:border-white/10 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <BrandLogo className="mr-3 hidden md:inline-flex" />
            <DashboardButton className="mr-4" />
            <div className="flex-1 min-w-0">
              <h1 className="text-lg font-semibold text-[#1b1033] dark:text-[#f4f0ff] font-display tracking-tight truncate flex items-center gap-2">
                <Wallet className="w-5 h-5 text-[#ff4d8d] shrink-0" />
                {t.account}
              </h1>
            </div>
            <div className="flex items-center gap-2 sm:gap-4 ml-4">
              <LanguageToggle />
              <ThemeMenu />
              <ProfileButton />
            </div>
          </div>
        </div>
      </header>
      <main className="flex-1 max-w-5xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {children}
      </main>
      <AppFooter />
    </div>
  )

  if (loading) {
    return shell(
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-6 h-6 animate-spin text-[#ff4d8d]" />
      </div>,
    )
  }

  if (error || !account) {
    return shell(
      <div className="rounded-2xl border border-[#1b1033]/8 dark:border-white/10 bg-white dark:bg-white/5 p-10 text-center">
        <p className="text-[#1b1033] dark:text-[#f4f0ff] font-medium">
          {tr('Could not load your account', 'ანგარიშის ჩატვირთვა ვერ მოხერხდა')}
        </p>
        <Link to="/dashboard">
          <Button variant="outline" className="mt-4 rounded-xl">
            <ArrowLeft className="w-4 h-4 mr-2" />
            {tr('Back to dashboard', 'დაფაზე დაბრუნება')}
          </Button>
        </Link>
      </div>,
    )
  }

  const balances = account.balances.filter((b) => b.amount !== 0)

  return shell(
    <>
      <div>
        <h2 className="font-display text-2xl font-bold tracking-tight text-[#1b1033] dark:text-[#f4f0ff]">
          {t.accountBalance}
        </h2>
        <p className="mt-1 text-sm text-[#4a3d66] dark:text-[#c3b8dd]">
          {t.accountSubtitle}
        </p>
      </div>

      {/* One card per currency — deliberately never a combined total. */}
      {balances.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[#1b1033]/15 dark:border-white/15 bg-white/60 dark:bg-white/5 p-10 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#fdeee9] dark:bg-[#281b47]">
            <Wallet className="w-7 h-7 text-[#ff4d8d]" />
          </div>
          <h3 className="text-xl font-semibold text-[#1b1033] dark:text-[#f4f0ff] font-display">
            {t.accountEmpty}
          </h3>
          <p className="mt-1 text-sm text-[#4a3d66] dark:text-[#c3b8dd] max-w-md mx-auto">
            {t.accountEmptyBody}
          </p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {balances.map((b) => (
            <div
              key={b.currency}
              className="rounded-2xl border border-[#1b1033]/8 dark:border-white/10 bg-white dark:bg-white/5 p-5 shadow-celebrate-sm"
              data-testid={`account-balance-${b.currency}`}
            >
              <p className="text-[11px] font-semibold uppercase tracking-wide text-[#4a3d66] dark:text-[#c3b8dd]">
                {t.balanceAvailable}
              </p>
              <p className="mt-1 font-display text-4xl font-bold tracking-tight text-[#1b1033] dark:text-[#f4f0ff] tabular-nums">
                {currencySymbol(b.currency)}
                {formatMoney(b.amount)}
                <span className="ml-2 text-base font-medium text-[#4a3d66] dark:text-[#c3b8dd]">
                  {b.currency}
                </span>
              </p>
            </div>
          ))}
        </div>
      )}

      {/* Referral programme */}
      {referral && (
        <div
          className="rounded-2xl border border-[#1b1033]/8 dark:border-white/10 bg-white dark:bg-white/5 p-5 shadow-celebrate-sm sm:p-6"
          data-testid="referral-card"
        >
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#8b5cf6]/12 text-[#8b5cf6]">
              <Sparkles className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <h3 className="font-display text-lg font-semibold tracking-tight text-[#1b1033] dark:text-[#f4f0ff]">
                {t.referralTitle}
              </h3>
              <p className="mt-1 text-sm text-[#4a3d66] dark:text-[#c3b8dd]">
                {t.referralBody}
              </p>
            </div>
          </div>

          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            <div>
              <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#4a3d66] dark:text-[#c3b8dd]">
                {t.referralCode}
              </p>
              <div className="flex items-center gap-2">
                <code
                  className="flex-1 min-w-0 truncate rounded-xl border border-[#1b1033]/10 bg-[#faf7fd] px-3 py-2.5 font-mono text-base font-semibold tracking-[0.22em] text-[#1b1033] dark:border-white/10 dark:bg-[#281b47] dark:text-[#f4f0ff]"
                  data-testid="referral-code-value"
                >
                  {referral.code}
                </code>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => copy(referral.code, 'code')}
                  className="h-11 w-11 shrink-0 rounded-xl"
                  aria-label={t.referralCode}
                  data-testid="copy-referral-code"
                >
                  {copied === 'code' ? (
                    <Check className="h-4 w-4 text-emerald-600" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
            <div>
              <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#4a3d66] dark:text-[#c3b8dd]">
                {t.referralLink}
              </p>
              <div className="flex items-center gap-2">
                <span
                  className="flex-1 min-w-0 truncate rounded-xl border border-[#1b1033]/10 bg-[#faf7fd] px-3 py-2.5 text-sm text-[#4a3d66] dark:border-white/10 dark:bg-[#281b47] dark:text-[#c3b8dd]"
                  data-testid="referral-link-value"
                >
                  {referral.link}
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => copy(referral.link, 'link')}
                  className="h-11 w-11 shrink-0 rounded-xl"
                  aria-label={t.referralLink}
                  data-testid="copy-referral-link"
                >
                  {copied === 'link' ? (
                    <Check className="h-4 w-4 text-emerald-600" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
          </div>

          <div className="mt-5 flex flex-wrap gap-2 border-t border-[#1b1033]/8 pt-4 dark:border-white/10">
            <span className="inline-flex items-center gap-2 rounded-full bg-[#f1ecf6] px-3 py-1.5 text-sm text-[#1b1033] dark:bg-[#281b47] dark:text-[#f4f0ff]">
              <Users className="h-3.5 w-3.5 text-[#8b5cf6]" />
              <span className="font-semibold tabular-nums">{referral.signups}</span>
              <span className="text-[#4a3d66] dark:text-[#c3b8dd]">
                {t.referralSignups}
              </span>
            </span>
            {referral.earned.length === 0 ? (
              <span className="inline-flex items-center gap-2 rounded-full bg-[#f1ecf6] px-3 py-1.5 text-sm text-[#4a3d66] dark:bg-[#281b47] dark:text-[#c3b8dd]">
                <Gift className="h-3.5 w-3.5 text-[#ff4d8d]" />
                {t.referralEarned}: —
              </span>
            ) : (
              referral.earned.map((e) => (
                <span
                  key={e.currency}
                  className="inline-flex items-center gap-2 rounded-full bg-[#f1ecf6] px-3 py-1.5 text-sm text-[#1b1033] dark:bg-[#281b47] dark:text-[#f4f0ff]"
                >
                  <Gift className="h-3.5 w-3.5 text-[#ff4d8d]" />
                  <span className="font-semibold tabular-nums">
                    {currencySymbol(e.currency)}
                    {formatMoney(e.amount)}
                  </span>
                  <span className="text-[#4a3d66] dark:text-[#c3b8dd]">
                    {t.referralEarned}
                  </span>
                </span>
              ))
            )}
          </div>
        </div>
      )}

      {/* Ledger */}
      <div className="rounded-2xl border border-[#1b1033]/8 dark:border-white/10 bg-white dark:bg-white/5 shadow-celebrate-sm overflow-hidden">
        <div className="border-b border-[#1b1033]/8 px-5 py-3.5 dark:border-white/10">
          <h3 className="font-display text-base font-semibold tracking-tight text-[#1b1033] dark:text-[#f4f0ff]">
            {t.accountHistory}
            {total > 0 && (
              <span className="ml-2 text-sm font-normal text-[#4a3d66] tabular-nums dark:text-[#c3b8dd]">
                {total}
              </span>
            )}
          </h3>
        </div>

        {entries.length === 0 ? (
          <p className="px-5 py-10 text-center text-sm text-[#4a3d66] dark:text-[#c3b8dd]">
            {t.accountNoHistory}
          </p>
        ) : (
          <ul className="divide-y divide-[#1b1033]/6 dark:divide-white/10">
            {entries.map((e) => (
              <li
                key={e.entry_id}
                className="flex items-start gap-3 px-5 py-3.5"
                data-testid={`account-entry-${e.entry_id}`}
              >
                <span
                  className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl ${
                    e.amount >= 0
                      ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                      : 'bg-[#ff4d8d]/10 text-[#ff4d8d]'
                  }`}
                >
                  {kindIcon(e.kind)}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-[#1b1033] dark:text-[#f4f0ff]">
                    {kindLabel(e.kind)}
                  </p>
                  {e.description && (
                    <p className="truncate text-[13px] text-[#4a3d66] dark:text-[#c3b8dd]">
                      {e.description}
                    </p>
                  )}
                  {e.created_at && (
                    <p className="text-xs text-[#4a3d66]/80 dark:text-[#c3b8dd]/80">
                      {dateFmt.format(new Date(e.created_at))}
                    </p>
                  )}
                </div>
                <div className="shrink-0 text-right tabular-nums">
                  <p
                    className={`text-sm font-semibold ${
                      e.amount >= 0
                        ? 'text-emerald-600 dark:text-emerald-400'
                        : 'text-[#ff4d8d]'
                    }`}
                  >
                    {e.amount >= 0 ? '+' : '−'}
                    {currencySymbol(e.currency)}
                    {formatMoney(Math.abs(e.amount))}
                  </p>
                  <p className="text-xs text-[#4a3d66] dark:text-[#c3b8dd]">
                    {t.balanceAfter} {currencySymbol(e.currency)}
                    {formatMoney(e.balance_after)}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        )}

        {entries.length < total && (
          <div className="border-t border-[#1b1033]/8 p-3 text-center dark:border-white/10">
            <Button
              type="button"
              variant="outline"
              onClick={loadMore}
              disabled={loadingMore}
              className="rounded-xl"
              data-testid="account-load-more"
            >
              {loadingMore ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                t.loadMore
              )}
            </Button>
          </div>
        )}
      </div>
    </>,
  )
}

export default Account
